import { Label } from '@ui/Label';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ChatBubbleOutlinedIcon from '@mui/icons-material/ChatBubbleOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import { useInterval } from 'usehooks-ts';
import { makeStyles } from 'tss-react/mui';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '@client/hooks/use-user.hooks';
import { isAdmin } from '@shared/helpers/user.helpers';
import {
  AiAdminConversationSummary,
  AiMessage,
  AiScopeTarget,
  AiToolCall,
} from '@shared/models/ai-chat.model';
import { tryCatchAwaitable } from '@shared/helpers/utils.helpers';
import {
  adminAiChatApi,
  AdminAiConversation,
  AdminUserOption,
} from './admin-ai-chat.api';
import { isHeavyJsonValue } from './json-viewer.helpers';
import { JsonViewer } from './JsonViewer';

const LIST_CAP = 50;

const QUERY_USER_ID = 'userId';
const QUERY_CONVERSATION_ID = 'conversationId';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  list: {
    width: '30%',
    minWidth: 320,
    maxWidth: 400,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  listHeader: {
    padding: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  listScroll: {
    overflowY: 'auto',
    flex: 1,
  },
  listRow: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  listRowActive: {
    background: `${theme.palette.primary.main}0a`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: `calc(${theme.spacing(3)} - 4px)`,
    '&:hover': {
      background: `${theme.palette.primary.main}14`,
    },
  },
  listRowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  listRowPreview: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    fontSize: 13,
    lineHeight: 1.4,
  },
  listRowMeta: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.5),
  },
  scopeChip: {
    height: 20,
    fontSize: 10,
    textTransform: 'uppercase',
    fontWeight: 700,
    backgroundColor: theme.palette.action.selected,
  },
  userLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  capNote: {
    padding: theme.spacing(2, 3),
    background: theme.palette.action.hover,
    textAlign: 'center',
  },
  detail: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  detailHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 2.5),
    borderRadius: theme.spacing(3.5),
    boxShadow: 'none',
    position: 'relative',
  },
  messageUser: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottomRightRadius: theme.spacing(0.5),
    maxWidth: '85%',
  },
  messageAssistant: {
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.spacing(0.5),
    maxWidth: '90%',
  },
  messageHeader: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  messageContent: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: 14,
    lineHeight: 1.5,
  },
  messageMeta: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
    opacity: 0.8,
  },
  toolCall: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    background: theme.palette.action.hover,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  toolCallHeader: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  toolCallName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: 13,
  },
  toolCallSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  toolCallFoldSummary: {
    cursor: 'pointer',
    listStyle: 'none',
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.primary.main,
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '&::-webkit-details-marker': { display: 'none' },
    '&::before': { content: '"▸ "' },
  },
  toolCallFoldSummaryOpen: {
    '&::before': { content: '"▾ "' },
  },
  notesList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  emptyStateIcon: {
    fontSize: 48,
    opacity: 0.5,
  },
}));

const describeScope = (
  target: AiScopeTarget,
  projectId: number | null,
): string => {
  const projectSuffix = projectId === null ? '' : ` #${projectId}`;
  switch (target.scope) {
    case 'organization':
      return projectId === null ? 'organization' : `project${projectSuffix}`;
    case 'project':
      return `project${projectSuffix}`;
    case 'design':
      return `design project${projectSuffix} / building ${target.buildingIndex}`;
    case 'elements':
      return `elements project${projectSuffix} / version ${target.versionId}${
        target.elementId ? ` / element ${target.elementId}` : ''
      }`;
  }
};

const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const getPreviewLabel = (c: AiAdminConversationSummary): string => {
  const preview = c.preview?.trim();
  return preview || '(empty)';
};

/**
 * Tool call renderer. The stored schema types {@link AiToolCall.result} as
 * `{ success, error? }`, but pipeline code persists the full tool return
 * (notes, data, etc.) in JSONB — see ai-chat.service runPipeline. Admin view
 * widens the type locally to surface those extra fields.
 */
const ToolCallBlock: React.FC<{ call: AiToolCall }> = ({ call }) => {
  const { classes, cx } = useStyles();
  const [resultOpen, setResultOpen] = useState(false);
  const argsHeavy = useMemo(
    () => isHeavyJsonValue(call.arguments),
    [call.arguments],
  );
  const [argsOpen, setArgsOpen] = useState(!argsHeavy);
  const result = call.result as
    | (AiToolCall['result'] & {
        notes?: unknown;
        data?: unknown;
      })
    | undefined;
  const success = result?.success;
  const chipColor: 'success' | 'error' | 'default' =
    success === true ? 'success' : success === false ? 'error' : 'default';
  const chipLabel =
    success === true ? 'success' : success === false ? 'failed' : 'pending';
  const notes = Array.isArray(result?.notes)
    ? (result!.notes.filter((n) => typeof n === 'string') as string[])
    : typeof result?.notes === 'string'
      ? [result.notes]
      : [];
  return (
    <Box className={classes.toolCall}>
      <Box className={classes.toolCallHeader}>
        <Typography variant="body2" className={classes.toolCallName}>
          {call.name}
        </Typography>
        <Label label={chipLabel} color={chipColor} />
      </Box>

      <Box className={classes.toolCallSection}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          Arguments
        </Typography>
        {argsHeavy ? (
          <details
            open={argsOpen}
            onToggle={(e) => setArgsOpen(e.currentTarget.open)}
          >
            <summary
              className={cx(classes.toolCallFoldSummary, {
                [classes.toolCallFoldSummaryOpen]: argsOpen,
              })}
            >
              Large payload (expand to render)
            </summary>
            {argsOpen && <JsonViewer data={call.arguments} />}
          </details>
        ) : (
          <JsonViewer data={call.arguments} />
        )}
      </Box>

      {result?.error && (
        <Box className={classes.toolCallSection}>
          <Typography variant="caption" color="error">
            Error
          </Typography>
          <Typography variant="body2" color="error">
            {result.error}
          </Typography>
        </Box>
      )}

      {notes.length > 0 && (
        <Box className={classes.toolCallSection}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Notes
          </Typography>
          <ul className={classes.notesList}>
            {notes.map((n, i) => (
              <li key={i}>
                <Typography variant="body2">{n}</Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}

      {result && (
        <details
          open={resultOpen}
          onToggle={(e) => setResultOpen(e.currentTarget.open)}
        >
          <summary
            className={cx(classes.toolCallFoldSummary, {
              [classes.toolCallFoldSummaryOpen]: resultOpen,
            })}
          >
            Full result
          </summary>
          {resultOpen && <JsonViewer data={result} />}
        </details>
      )}
    </Box>
  );
};

const MessageBlock: React.FC<{ message: AiMessage }> = ({ message }) => {
  const { classes, cx } = useStyles();
  const isUser = message.role === 'user';

  return (
    <Box
      className={cx(classes.message, {
        [classes.messageUser]: isUser,
        [classes.messageAssistant]: !isUser,
      })}
    >
      <Box className={classes.messageHeader}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}
        >
          {message.role}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.8 }}>
          {formatDateTime(message.createdAt)}
        </Typography>
      </Box>
      {message.content && (
        <Typography className={classes.messageContent}>
          {message.content}
        </Typography>
      )}
      {message.toolCalls?.map((call, i) => (
        <ToolCallBlock key={i} call={call} />
      ))}
      <Box className={classes.messageMeta}>
        {message.status !== 'processed' && (
          <Label
            label={message.status}
            sx={{
              height: 16,
              fontSize: 9,
              color: 'inherit',
              borderColor: 'inherit',
            }}
          />
        )}
        {message.scopeTarget && (
          <Label
            label={describeMessageScope(message.scopeTarget)}
            sx={{
              height: 16,
              fontSize: 9,
              color: 'inherit',
              borderColor: 'inherit',
            }}
          />
        )}
        {message.model && (
          <Typography variant="caption" sx={{ fontSize: 9 }}>
            {message.model}
          </Typography>
        )}
        {message.responseTimeMs != null && (
          <Typography variant="caption" sx={{ fontSize: 9 }}>
            {message.responseTimeMs}ms
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** Compact label for the per-message scope chip — projectId lives on the conversation, so omit it here. */
const describeMessageScope = (target: AiScopeTarget): string => {
  switch (target.scope) {
    case 'organization':
      return 'organization';
    case 'project':
      return 'project';
    case 'design':
      return `design / building ${target.buildingIndex}`;
    case 'elements':
      return `elements / version ${target.versionId}${
        target.elementId ? ` / element ${target.elementId}` : ''
      }`;
  }
};

export const AdminAIConversations: React.FC = () => {
  const { classes, cx } = useStyles();
  const user = useUser();
  const disabled = useMemo(() => !user || !isAdmin(user), [user]);
  const [searchParams, setSearchParams] = useSearchParams();

  const listUserId = searchParams.get(QUERY_USER_ID) ?? user?.id ?? undefined;
  const selectedId = searchParams.get(QUERY_CONVERSATION_ID) ?? undefined;

  const [list, setList] = useState<AiAdminConversationSummary[] | undefined>();
  const [limit, setLimit] = useState<number>(LIST_CAP);
  const [conversation, setConversation] = useState<
    AdminAiConversation | undefined
  >();
  const [admins, setAdmins] = useState<AdminUserOption[] | undefined>();
  const [listError, setListError] = useState<string | undefined>();
  const [conversationError, setConversationError] = useState<
    string | undefined
  >();

  const selectedIdRef = useRef<string | undefined>(selectedId);
  selectedIdRef.current = selectedId;

  const prevListUserIdRef = useRef<string | null>(null);

  const loadList = useCallback(async (forUserId?: string) => {
    const res = await tryCatchAwaitable(adminAiChatApi.list(forUserId));
    if (!res.ok) {
      setListError(res.error.message || 'Failed to load conversations');
      return;
    }
    setList(res.value.conversations);
    setLimit(res.value.limit);
    setListError(undefined);
  }, []);

  const loadAdmins = useCallback(async () => {
    const res = await tryCatchAwaitable(adminAiChatApi.listUsers());
    if (!res.ok) return;
    setAdmins(res.value.admins);
  }, []);

  const loadConversation = useCallback(async (id: string) => {
    const res = await tryCatchAwaitable(adminAiChatApi.get(id));
    if (selectedIdRef.current !== id) return;
    if (!res.ok) {
      setConversation(undefined);
      setConversationError(res.error.message || 'Failed to load conversation');
      return;
    }
    setConversation(res.value);
    setConversationError(undefined);
  }, []);

  useEffect(() => {
    if (disabled) return;
    loadAdmins().catch(console.error);
  }, [disabled, loadAdmins]);

  useEffect(() => {
    if (disabled || !listUserId) return;
    if (prevListUserIdRef.current !== listUserId) {
      setList(undefined);
      prevListUserIdRef.current = listUserId;
    }
    loadList(listUserId).catch(console.error);
  }, [disabled, listUserId, loadList]);

  useEffect(() => {
    if (!selectedId) {
      setConversation(undefined);
      setConversationError(undefined);
      return;
    }
    setConversation(undefined);
    setConversationError(undefined);
    loadConversation(selectedId).catch(console.error);
  }, [selectedId, loadConversation]);

  // Poll list and active conversation
  useInterval(() => {
    if (disabled || !listUserId) return;
    loadList(listUserId).catch(console.error);
    if (selectedId) {
      loadConversation(selectedId).catch(console.error);
    }
  }, 20000);

  const handleTargetChange = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set(QUERY_USER_ID, next);
          p.delete(QUERY_CONVERSATION_ID);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSelectConversation = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set(QUERY_CONVERSATION_ID, id);
          if (!p.get(QUERY_USER_ID) && user?.id) {
            p.set(QUERY_USER_ID, user.id);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams, user?.id],
  );

  if (disabled) {
    return (
      <Box
        sx={{
          p: 4,
        }}
      >
        <Typography variant="h6">Not allowed</Typography>
      </Box>
    );
  }

  const hitCap = !!list && list.length >= limit;

  return (
    <Box className={classes.root}>
      <Box className={classes.list}>
        <Box className={classes.listHeader}>
          <FormControl size="small" fullWidth>
            <InputLabel id="admin-ai-user-select-label">Admin</InputLabel>
            <Select
              labelId="admin-ai-user-select-label"
              label="Admin"
              value={listUserId ?? ''}
              onChange={(e) => handleTargetChange(e.target.value)}
            >
              {user?.id && (
                <MenuItem value={user.id}>
                  {user.nickname || user.name} ({user.name}) (you)
                </MenuItem>
              )}
              {admins
                ?.filter((a) => a.userId !== user?.id)
                .map((a) => (
                  <MenuItem key={a.userId} value={a.userId}>
                    {a.userName ?? a.userId}
                    {a.email ? ` (${a.email})` : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Newest first
          </Typography>
        </Box>
        {listError && (
          <Box
            sx={{
              p: 3,
            }}
          >
            <Typography variant="body2" color="error">
              {listError}
            </Typography>
          </Box>
        )}
        {!list && !listError && (
          <Box className={classes.emptyState}>
            <Typography variant="body2">Loading…</Typography>
          </Box>
        )}
        <Box className={classes.listScroll}>
          {list && list.length === 0 && (
            <Box className={classes.emptyState}>
              <InboxOutlinedIcon className={classes.emptyStateIcon} />
              <Typography variant="body2">No conversations found.</Typography>
            </Box>
          )}
          {list?.map((c) => (
            <Box
              key={c.id}
              className={cx(classes.listRow, {
                [classes.listRowActive]: c.id === selectedId,
              })}
              onClick={() => handleSelectConversation(c.id)}
            >
              <Box className={classes.listRowHeader}>
                <Typography className={classes.userLabel} noWrap>
                  {c.userName ?? '(unknown user)'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontSize: 10,
                  }}
                >
                  {formatDateTime(c.createdAt)}
                </Typography>
              </Box>
              <Typography variant="body2" className={classes.listRowPreview}>
                {getPreviewLabel(c)}
              </Typography>
              <Box className={classes.listRowMeta}>
                <Label
                  label={c.scopeTarget.scope}
                  className={classes.scopeChip}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontSize: 10,
                  }}
                >
                  {c.organizationId}
                </Typography>
              </Box>
            </Box>
          ))}
          {hitCap && (
            <Box className={classes.capNote}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Showing latest {limit}. There may be more — pagination not
                implemented (too lazy).
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box className={classes.detail}>
        {!selectedId && (
          <Box className={classes.emptyState}>
            <ChatBubbleOutlinedIcon className={classes.emptyStateIcon} />
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
              }}
            >
              No conversation selected
            </Typography>
            <Typography variant="body2">
              Select a conversation from the list to view its details.
            </Typography>
          </Box>
        )}
        {selectedId &&
          (!conversation || conversation.id !== selectedId) &&
          !conversationError && (
            <Box className={classes.emptyState}>
              <Typography variant="body2">Loading conversation…</Typography>
            </Box>
          )}
        {selectedId && conversationError && (
          <Box className={classes.emptyState}>
            <ErrorOutlinedIcon
              className={classes.emptyStateIcon}
              color="error"
            />
            <Typography variant="body2" color="error">
              {conversationError}
            </Typography>
          </Box>
        )}
        {conversation && conversation.id === selectedId && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              maxWidth: 1000,
              margin: '0 auto',
              width: '100%',
            }}
          >
            <Box className={classes.detailHeader}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                }}
              >
                {conversation.title?.trim() || '(untitled)'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {describeScope(
                  conversation.scopeTarget,
                  conversation.projectId,
                )}{' '}
                · {conversation.userName ?? '(unknown user)'} ·{' '}
                {conversation.organizationId}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                created {formatDateTime(conversation.createdAt)} · updated{' '}
                {formatDateTime(conversation.updatedAt)} · status{' '}
                {conversation.status}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {conversation.messages.map((m) => (
                <MessageBlock key={m.id} message={m} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
